import { useEffect } from 'react';

import { ButtonEditorLinkToggles } from '@/components/shared/button-editor';
import {
  getPublishedPageSearchEmptyText,
  usePublishedPageSearch,
} from '@/components/shared/use-published-page-search';
import { SelectInput, TextInput } from '@/components/ui/form';
import { Icon } from '@/components/ui/icon';
import {
  BUTTON_LINK_TYPE_OPTIONS,
  getButtonLinkTypePlaceholder,
  isExternalLinkType,
  isPageLinkType,
} from '@/lib/blocks/button-links';
import { getPageDisplayPath, getPageDisplayTitle } from '@/lib/page-utils';
import { usePublishedPagesState } from '@/stores/pages-store';
import type { ButtonLinkType } from '@/types/button';
import type { Page } from '@/types/page';

export interface LinkSettingsValue {
  linkType: ButtonLinkType;
  link?: string;
  internalPageId?: string;
  openInNewTab?: boolean;
  nofollow?: boolean;
}

interface LinkSettingsFieldsProps {
  value: LinkSettingsValue;
  onChange: (value: LinkSettingsValue) => void;
  enableSearch: boolean;
  onPageSelect?: (page: Page | undefined) => void;
  allowPageOpenInNewTab?: boolean;
  showNofollow?: boolean;
}

function supportsOpenInNewTab(linkType: ButtonLinkType, allowPageOpenInNewTab: boolean) {
  return isExternalLinkType(linkType) || (allowPageOpenInNewTab && isPageLinkType(linkType));
}

/** Shared link destination controls used by button and rich-text editors. */
export function LinkSettingsFields({
  value,
  onChange,
  enableSearch,
  onPageSelect,
  allowPageOpenInNewTab = false,
  showNofollow = true,
}: LinkSettingsFieldsProps) {
  const {
    publishedPages,
    hasLoaded: hasPublishedPagesLoaded,
    isLoading: isPublishedPagesLoading,
  } = usePublishedPagesState();
  const {
    pages: pageOptions,
    hasLoaded,
    isLoading,
    searchQuery,
    setSearchQuery,
  } = usePublishedPageSearch({
    enabled: enableSearch && value.linkType === 'page',
    selectedPageId: value.internalPageId,
    initialPages: publishedPages,
    hasInitialPagesLoaded: hasPublishedPagesLoaded,
    isInitialLoading: isPublishedPagesLoading,
  });
  const selectedPage = pageOptions.find((page) => page.id === value.internalPageId);
  const showOpenInNewTab = supportsOpenInNewTab(value.linkType, allowPageOpenInNewTab);
  const showExternalNofollow = showNofollow && isExternalLinkType(value.linkType);

  useEffect(() => {
    onPageSelect?.(selectedPage);
  }, [onPageSelect, selectedPage]);

  const updateValue = (updates: Partial<LinkSettingsValue>) => {
    onChange({ ...value, ...updates });
  };

  return (
    <div className="flex flex-col gap-3">
      <SelectInput
        label="Link To"
        value={value.linkType}
        onValueChange={(nextValue) => {
          const nextLinkType = nextValue as ButtonLinkType;
          const nextTypeSupportsNewTab = supportsOpenInNewTab(nextLinkType, allowPageOpenInNewTab);
          updateValue({
            linkType: nextLinkType,
            internalPageId: nextLinkType === 'page' ? value.internalPageId : undefined,
            openInNewTab: nextTypeSupportsNewTab ? value.openInNewTab : false,
            nofollow: showNofollow && nextLinkType === 'external' ? value.nofollow : false,
          });
        }}
        searchable={false}
        options={BUTTON_LINK_TYPE_OPTIONS.map((option) => ({
          value: option.value,
          title: option.label,
          icon: <Icon icon={option.icon} className="text-foreground" />,
        }))}
        contentClassName="z-[150]"
        valueContent={(option) =>
          option ? (
            <span className="flex items-center gap-2">
              {option.icon}
              <span>{option.title}</span>
            </span>
          ) : (
            'Select link type'
          )
        }
      />

      {isPageLinkType(value.linkType) ? (
        <SelectInput
          value={value.internalPageId}
          onValueChange={(pageId) => {
            updateValue({ internalPageId: pageId });
          }}
          placeholder={isLoading ? 'Loading pages...' : 'Select page'}
          emptyText={getPublishedPageSearchEmptyText({ hasLoaded, searchQuery })}
          searchPlaceholder="Search pages..."
          contentClassName="z-[150]"
          searchValue={searchQuery}
          onSearchValueChange={setSearchQuery}
          shouldFilter={false}
          options={pageOptions.map((page) => ({
            value: page.id,
            title: getPageDisplayTitle(page),
            description: getPageDisplayPath(page),
          }))}
        />
      ) : (
        <TextInput
          value={value.link || ''}
          onChange={(event) => updateValue({ link: event.target.value })}
          placeholder={getButtonLinkTypePlaceholder(value.linkType)}
        />
      )}

      {showOpenInNewTab || showExternalNofollow ? (
        <ButtonEditorLinkToggles
          openInNewTab={value.openInNewTab === true}
          nofollow={value.nofollow === true}
          onOpenInNewTabChange={(checked) => updateValue({ openInNewTab: checked })}
          onNofollowChange={(checked) => updateValue({ nofollow: checked })}
          showOpenInNewTab={showOpenInNewTab}
          showNofollow={showExternalNofollow}
        />
      ) : null}
    </div>
  );
}
